import React from 'react';
import TeamSection from '../components/TeamSection';
import { useAppData } from '../AppContext';
import './About.css';

// Icons are fixed SVGs keyed by position (title/desc come from admin)
const EXPERTISE_ICONS = [
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/><path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/><path d="M12 3v3m0 12v3M3 12h3m12 0h3"/><circle cx="12" cy="12" r="3"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M3 12h18M3 18h18M6 3v18M12 3v18M18 3v18" opacity=".3"/><rect x="5" y="4" width="14" height="16" rx="2"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="8" y="2" width="8" height="20" rx="2"/><path d="M8 7h8M8 12h8M8 17h8"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2c-2 4-4 6-4 10a4 4 0 0 0 8 0c0-4-2-6-4-10z"/><path d="M9 18v3m6-3v3"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 8h20v12H2z"/><path d="M8 8V4h8v4"/><path d="M5 20v2m14-2v2M12 12v5m-3-2.5h6"/>
  </svg>,
];

const STAT_ICONS = [
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>,
];

const JGZT_ICONS = [
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><path d="M8 21h8M12 17v4"/>
  </svg>,
];

const About = () => {
  const { about } = useAppData();
  const { hero, intro, mission, vision, expertise, jgzt } = about;

  return (
    <div className="ab-page">

      {/* Hero */}
      <div className="ab-hero">
        <div className="ab-hero-overlay" />
        <div className="ab-hero-inner">
          <span className="ab-badge">{hero.badge}</span>
          <h1 className="ab-title">{hero.title}</h1>
          <p className="ab-subtitle">{hero.subtitle}</p>
          <div className="ab-stats">
            {hero.stats.map((stat, i) => (
              <div className="ab-stat" key={stat.id}>
                <div className="ab-stat-icon">{STAT_ICONS[i]}</div>
                <div className="ab-stat-number">{stat.value}</div>
                <div className="ab-stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="ab-hero-wave">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
            <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#f4f8fd"/>
          </svg>
        </div>
      </div>

      {/* About JGI */}
      <section className="ab-section ab-section-light">
        <div className="ab-container">
          <div className="ab-intro-grid">
            <div className="ab-intro-text">
              <p className="ab-section-tag">{intro.tag}</p>
              <h2 className="ab-section-heading">{intro.heading}</h2>
              <p className="ab-intro-para">{intro.paragraph}</p>
              <ul className="ab-highlights">
                {intro.highlights.map((item, i) => (
                  <li key={i}>
                    <span className="ab-check">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="ab-mission-card">
              <div className="ab-mission-block">
                <div className="ab-mission-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
                  </svg>
                </div>
                <h3>{mission.heading}</h3>
                <p>{mission.text}</p>
              </div>
              <div className="ab-mission-divider" />
              <div className="ab-mission-block">
                <div className="ab-mission-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                  </svg>
                </div>
                <h3>{vision.heading}</h3>
                <p>{vision.text}</p>
              </div>
            </div>
          </div>

          {/* Expertise Grid */}
          <div>
            <p className="ab-section-tag" style={{ textAlign: 'center' }}>{expertise.tag}</p>
            <h2 className="ab-section-heading" style={{ textAlign: 'center', marginBottom: '2.5rem' }}>{expertise.heading}</h2>
            <div className="ab-expertise-grid">
              {expertise.items.map((item, i) => (
                <div className="ab-exp-card" key={item.id}>
                  <div className="ab-exp-icon">{EXPERTISE_ICONS[i]}</div>
                  <h4 className="ab-exp-title">{item.title}</h4>
                  <p className="ab-exp-desc">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* JGZT */}
      <section className="ab-section ab-section-navy">
        <div className="ab-container">
          <p className="ab-section-tag ab-tag-light" style={{ textAlign: 'center' }}>{jgzt.tag}</p>
          <h2 className="ab-section-heading ab-heading-light" style={{ textAlign: 'center', marginBottom: '3rem' }}>
            {jgzt.heading}
          </h2>
          <div className="ab-zinga-grid">
            {jgzt.cards.map((card, i) => (
              <div className={`ab-zinga-card${i === 1 ? ' ab-zinga-card-accent' : ''}`} key={card.id}>
                <div className="ab-zinga-top">
                  <div className="ab-zc-icon">{JGZT_ICONS[i]}</div>
                  <span className="ab-zinga-tag">{card.tag}</span>
                </div>
                <h3>{card.heading}</h3>
                <p>{card.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TeamSection />
    </div>
  );
};

export default About;
